import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { env } from '@/env';

// =============================================================================
// Types
// =============================================================================

export interface UploadTokenInfo {
  tenantName: string;
  customerName?: string;
  sprawaSignature?: string;
  expiresAt?: string;
  message?: string;
  maxSizeBytes: number;
  totalUploadedBytes: number;
  remainingBytes: number;
  turnstileSiteKey: string;
}

export interface CustomerUploadResult {
  fileName: string;
  uploadedAt: string;
  fileSizeBytes: number;
}

export interface ImportProviderConfig {
  google: {
    clientId: string;
    appId: string;
    apiKey: string;
  };
  microsoft: {
    clientId: string;
  };
}

// =============================================================================
// Public Upload API — no auth tokens
// =============================================================================

export const publicUploadApi = createApi({
  reducerPath: 'publicUploadApi',
  baseQuery: fetchBaseQuery({
    baseUrl: env.api.baseUrl,
  }),
  tagTypes: ['PublicUploadToken'],
  endpoints: (builder) => ({
    validateUploadToken: builder.query<UploadTokenInfo, string>({
      query: (token) => `/public/upload/${token}`,
      providesTags: (_result, _error, token) => [{ type: 'PublicUploadToken', id: token }],
    }),

    uploadViaLink: builder.mutation<
      CustomerUploadResult,
      { token: string; fileName: string; turnstileToken: string; file: File }
    >({
      query: ({ token, fileName, turnstileToken, file }) => {
        const params = new URLSearchParams({ fileName, turnstileToken });
        return {
          url: `/public/upload/${token}/documents?${params.toString()}`,
          method: 'POST',
          body: file,
        };
      },
      invalidatesTags: (_result, _error, { token }) => [{ type: 'PublicUploadToken', id: token }],
    }),

    getPublicImportConfig: builder.query<ImportProviderConfig, string>({
      query: (token) => `/public/upload/${token}/import-config`,
    }),
  }),
});

export const {
  useValidateUploadTokenQuery,
  useUploadViaLinkMutation,
  useGetPublicImportConfigQuery,
} = publicUploadApi;
